import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrain, faTrainSubway, faExchangeAlt, faSpinner, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import KtmbApiService from './KtmbApiService';
import './KtmbTimetable.css';
import KtmbCalendar from './KtmbCalendar';
import KtmbTimetableResult from './KtmbTimetableResult';



function groupStations(stations) {
  // Group by country, then by state
  const grouped = {};
  stations.forEach(station => {
    if (!grouped[station.country]) grouped[station.country] = {};
    if (!grouped[station.country][station.state]) grouped[station.country][station.state] = [];
    grouped[station.country][station.state].push(station);
  });
  // Convert to react-select group format
  return Object.entries(grouped).map(([country, states]) => ({
    label: country,
    options: Object.entries(states).flatMap(([state, stations]) => [
      { label: `— ${state} —`, isDisabled: true },
      ...stations.map(station => {
        return {
          value: station.stationId,
          label: station.stationName,
          state: state,
          trainServices: station.trainServices || [],
          station,
        };
      })
    ])
  }));
}

// Custom Option component for displaying badges
const CustomOption = ({ data, ...props }) => {
  if (data.isDisabled) {
    return <div {...props.innerProps} style={{ padding: '8px 12px', fontWeight: 600, color: '#6b7280' }}>{data.label}</div>;
  }
  
  // Color mapping for different train services
  const getServiceColor = (service) => {
    const serviceUpper = service.toUpperCase();
    switch (serviceUpper) {
      case 'AFC':
        return '#10b981'; // Green
      case 'INTERCITY':
        return '#3b82f6'; // Blue
      case 'ETS':
        return '#8b5cf6'; // Purple
      default:
        return '#6b7280'; // Gray for unknown services
    }
  };
  
  return (
    <div {...props.innerProps} style={{ padding: '8px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 500 }}>
          {data.label}
        </div>
        {data.trainServices && data.trainServices.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {data.trainServices.map((service, index) => (
              <span
                key={index}
                style={{
                  background: getServiceColor(service),
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {service}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const trainTabs = [
  { label: 'ETS / Intercity', icon: 'train', active: true }
  // { label: 'KTM Komuter', icon: 'train-subway', active: false },  
  // { label: 'Shuttle Terbau', icon: 'train-subway', active: false },
];

const getMalaysiaCurrentDate = () => {
  // Get current date in Malaysia timezone (Asia/Kuala_Lumpur)
  const now = new Date();
  const malaysiaNow = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
  // Format as yyyy-MM-dd
  const yyyy = malaysiaNow.getFullYear();
  const mm = String(malaysiaNow.getMonth() + 1).padStart(2, '0');
  const dd = String(malaysiaNow.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};



const KtmbTimetable = () => {
  const [stations, setStations] = useState([]);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [passenger, setPassenger] = useState(1);
  const [date, setDate] = useState(() => {
    return getMalaysiaCurrentDate();
  });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [timetableData, setTimetableData] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showStationOverlay, setShowStationOverlay] = useState(null); // 'origin' | 'destination' | null

  const getTabDates = () => {
    const base = new Date(date);
    base.setHours(0,0,0,0);
    return [-2, -1, 0, 1, 2].map(offset => {
      const d = new Date(base);
      d.setDate(base.getDate() + offset);
      return d;
    });
  };

  const [tabDate, setTabDate] = useState(() => {
    // Malaysia current date at 00:00
    const now = new Date();
    const malaysiaNow = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    malaysiaNow.setHours(0,0,0,0);
    return malaysiaNow;
  });

  const ktmbApiService = new KtmbApiService();

  useEffect(() => {
    setLoading(true);
    const filter = activeTabIndex === 1 ? 'AFC' : undefined;
    ktmbApiService.getStations(filter)
      .then(data => {
        setStations(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    setOrigin(null);
    setDestination(null);
  }, [activeTabIndex]);

  const groupedOptions = groupStations(stations);

  // Custom filter function to search by both station name and state
  const customFilterOption = (option, inputValue) => {
    const stationName = option.data.label || '';
    const stateName = option.data.state || '';
    const input = inputValue.toLowerCase();
    
    return stationName.toLowerCase().includes(input) || 
           stateName.toLowerCase().includes(input);
  };

  // Create filtered destination options based on origin selection
  const getFilteredDestinationOptions = () => {
    if (!origin || !origin.trainServices || origin.trainServices.length === 0) {
      return groupedOptions;
    }
    
    // Get train services from origin station
    const originServices = origin.trainServices;
    
    // Filter stations that have at least one matching train service
    const filteredStations = stations.filter(station => {
      if (!station.trainServices || station.trainServices.length === 0) return false;
      return station.trainServices.some(service => 
        originServices.some(originService => 
          originService.toUpperCase() === service.toUpperCase()
        )
      );
    });
    
    // Group filtered stations by country and state
    const grouped = {};
    filteredStations.forEach(station => {
      if (!grouped[station.country]) grouped[station.country] = {};
      if (!grouped[station.country][station.state]) grouped[station.country][station.state] = [];
      grouped[station.country][station.state].push(station);
    });
    
    return Object.entries(grouped).map(([country, states]) => ({
      label: country,
      options: Object.entries(states).flatMap(([state, stations]) => [
        { label: `— ${state} —`, isDisabled: true },
        ...stations.map(station => {
          return {
            value: station.stationId,
            label: station.stationName,
            state: state,
            trainServices: station.trainServices || [],
            station,
          };
        })
      ])
    }));
  };

  const filteredDestinationOptions = getFilteredDestinationOptions();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!origin || !destination) {
      setErrorMsg('Please select both origin and destination.');
      return;
    }
    setSearchLoading(true);
    setErrorMsg('');
    // Format date in Malaysia timezone (UTC+8) as yyyy-MM-dd
    const selectedDate = new Date(tabDate);
    const malaysiaTime = new Date(selectedDate.toLocaleString("en-US", {timeZone: "Asia/Kuala_Lumpur"}));
    const yyyy = malaysiaTime.getFullYear();
    const mm = String(malaysiaTime.getMonth() + 1).padStart(2, '0');
    const dd = String(malaysiaTime.getDate()).padStart(2, '0');
    const departureDate = `${yyyy}-${mm}-${dd}`;
    try {
      let data;
      if (activeTabIndex === 1) {
        data = await ktmbApiService.getKomuterTimeTable(
          origin.station.stationId,
          destination.station.stationId,
          departureDate
        );
      } else {
        data = await ktmbApiService.getTimeTable(
          origin.station.stationId,
          destination.station.stationId,
          departureDate,
          passenger
        );
      }
      if (data.error) {
        setErrorMsg(data.error);
        setTimetableData(null);
      } else {
        setTimetableData(data);
      }
    } catch (error) {
      setErrorMsg(error.toString());
      setTimetableData(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setTimetableData(null);
  };

  const handleSwap = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
  };

  const handleDateClick = (e) => {
    e.preventDefault();
    setShowCalendar(true);
  };

  const handleCalendarClose = () => {
    setShowCalendar(false);
  };

  const handleDateSelect = (selectedDate) => {
    // Convert the selected date to Malaysia timezone
    const malaysiaDate = new Date(selectedDate.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    const year = malaysiaDate.getFullYear();
    const month = String(malaysiaDate.getMonth() + 1).padStart(2, '0');
    const day = String(malaysiaDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setDate(formattedDate);
    setTabDate(malaysiaDate);
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const formatCalendarDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isToday = (date) => {
    // Get today's date in Malaysia timezone
    const today = new Date();
    const malaysiaToday = new Date(today.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    const malaysiaDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    return malaysiaDate.toDateString() === malaysiaToday.toDateString();
  };

  const isSelected = (calendarDate) => {
    // Convert the calendar date to Malaysia timezone for comparison
    const malaysiaCalendarDate = new Date(calendarDate.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    const year = malaysiaCalendarDate.getFullYear();
    const month = String(malaysiaCalendarDate.getMonth() + 1).padStart(2, '0');
    const day = String(malaysiaCalendarDate.getDate()).padStart(2, '0');
    const formattedCalendarDate = `${year}-${month}-${day}`;
    
    return formattedCalendarDate === date;
  };

  const isPastDate = (date) => {
    // Get today's date in Malaysia timezone
    const today = new Date();
    const malaysiaToday = new Date(today.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    malaysiaToday.setHours(0, 0, 0, 0);
    const malaysiaDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    return malaysiaDate < malaysiaToday;
  };

  const nextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  // Extract the timetable fetch logic into a function fetchTimetableForDate
  const fetchTimetableForDate = async (newDate) => {
    if (!origin || !destination) return;
    setSearchLoading(true);
    setErrorMsg('');
    // Format date in Malaysia timezone (UTC+8) as yyyy-MM-dd
    const selectedDate = new Date(newDate);
    const malaysiaTime = new Date(selectedDate.toLocaleString("en-US", {timeZone: "Asia/Kuala_Lumpur"}));
    const yyyy = malaysiaTime.getFullYear();
    const mm = String(malaysiaTime.getMonth() + 1).padStart(2, '0');
    const dd = String(malaysiaTime.getDate()).padStart(2, '0');
    const departureDate = `${yyyy}-${mm}-${dd}`;
    try {
      let data;
      if (activeTabIndex === 1) {
        data = await ktmbApiService.getKomuterTimeTable(
          origin.station.stationId,
          destination.station.stationId,
          departureDate
        );
      } else {
        data = await ktmbApiService.getTimeTable(
          origin.station.stationId,
          destination.station.stationId,
          departureDate,
          passenger
        );
      }
      if (data.error) {
        setErrorMsg(data.error);
        setTimetableData(null);
      } else {
        setTimetableData(data);
      }
    } catch (error) {
      setErrorMsg(error.toString());
      setTimetableData(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // If we have timetable data, show the results
  if (timetableData) {
    return (
      <KtmbTimetableResult
        timetableData={timetableData}
        errorMsg={errorMsg}
        searchLoading={searchLoading}
        onBack={handleBackToSearch}
        tabDate={tabDate}
        setTabDate={setTabDate}
        setDate={setDate}
        fetchTimetableForDate={fetchTimetableForDate}
      />
    );
  }

  // Show search form
  return (
    <div className="ktmb-timetable-bg compact">
      <div className="ktmb-timetable-card compact ktmb-card">
        <div className="ktmb-schedule-card">
          <div className="ktmb-schedule-card-icon">
            <span>🚆</span>
          </div>
          <div className="ktmb-schedule-card-title">KTMB Schedule</div>
          <div className="ktmb-schedule-card-desc">Plan your journey and get your ticket in one click.</div>
        </div>
        <br/>
        <div className="ktmb-tab-bar">
          {trainTabs.map((tab, idx) => (
            <div
              key={tab.label}
              className={`ktmb-tab${idx === activeTabIndex ? ' active' : ''}`}
              onClick={() => setActiveTabIndex(idx)}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={tab.icon === 'train' ? faTrain : faTrainSubway} />
              <span>{tab.label}</span>
            </div>
          ))}
        </div>
        <form className="ktmb-form compact" onSubmit={handleSubmit}>
          {errorMsg && (
            <div className="ktmb-error-alert">{errorMsg}</div>
          )}
          <div className="ktmb-select-group">
            <div className="ktmb-input-group">
              <label htmlFor="origin" className="ktmb-label">Origin</label>
              <div onClick={() => setShowStationOverlay('origin')}>
                <input
                  id="origin"
                  className="ktmb-input ktmb-station-input"
                  value={origin ? origin.label : ''}
                  placeholder="Origin..."
                  readOnly
                  style={{ cursor: 'pointer', background: '#fafafa' }}
                />
              </div>
            </div>
            <button
              type="button"
              className="ktmb-swap-btn"
              onClick={handleSwap}
              tabIndex={-1}
              aria-label="Swap Origin and Destination"
            >
              <FontAwesomeIcon icon={faExchangeAlt} />
            </button>
            <div className="ktmb-input-group">
              <label htmlFor="destination" className="ktmb-label">Destination</label>
              <div onClick={() => setShowStationOverlay('destination')}>
                <input
                  id="destination"
                  className="ktmb-input ktmb-station-input"
                  value={destination ? destination.label : ''}
                  placeholder="Destination..."
                  readOnly
                  style={{ cursor: 'pointer', background: '#fafafa' }}
                />
              </div>
            </div>
            {/* Only show pax input if not KTM Komuter tab */}
            {activeTabIndex !== 1 && (
              <div className="ktmb-input-group">
                <label htmlFor="pax" className="ktmb-label">Passenger(s)</label>
                <input
                  id="pax"
                  type="number"
                  min="1"
                  max="6"
                  className="ktmb-input"
                  value={passenger}
                  onChange={e => setPassenger(Number(e.target.value))}
                />
              </div>
            )}
             <div className="ktmb-input-group">
              <label htmlFor="date" className="ktmb-label">Date</label>
              <input
                id="date"
                type="text"
                value={new Date(date).toLocaleDateString('en-US', { 
                  weekday: 'short',
                  month: 'short', 
                  day: 'numeric' 
                })}
                onClick={handleDateClick}
                readOnly
                className="ktmb-input ktmb-date-input"
                placeholder="Select date..."
              />
            </div>

            <button 
          className="ktmb-search-btn compact" 
          type="submit" 
          onClick={handleSubmit}
          disabled={searchLoading}
        >
          {searchLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
              Searching...
            </>
          ) : (
            'Search'
          )}
        </button>
          </div>
        </form>
    
      </div>
      
      {showCalendar && (
  <KtmbCalendar
    calendarDate={calendarDate}
    getDaysInMonth={getDaysInMonth}
    formatCalendarDate={formatCalendarDate}
    isToday={isToday}
    isSelected={isSelected}
    isPastDate={isPastDate}
    onDateSelect={handleDateSelect}
    onClose={handleCalendarClose}
    onPrevMonth={prevMonth}
    onNextMonth={nextMonth}
  />
)}
{showStationOverlay && (
  <div className="ktmb-station-overlay">
    <div className="ktmb-station-modal">
      <div className="ktmb-calendar-header">
        <button className="ktmb-calendar-close" onClick={() => setShowStationOverlay(null)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <h2 className="ktmb-calendar-title">Select {showStationOverlay === 'origin' ? 'Origin' : 'Destination'}</h2>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 16 }}>
        <Select
          autoFocus
          classNamePrefix="ktmb-select"
          isSearchable
          isLoading={loading}
          options={showStationOverlay === 'origin' ? groupedOptions : filteredDestinationOptions}
          value={showStationOverlay === 'origin' ? origin : destination}
          onChange={value => {
            if (showStationOverlay === 'origin') setOrigin(value);
            else setDestination(value);
            setShowStationOverlay(null);
            setErrorMsg('');
          }}
          placeholder={showStationOverlay === 'origin' ? 'Origin...' : 'Destination...'}
          formatGroupLabel={group => <span style={{ fontWeight: 600 }}>{group.label}</span>}
          styles={{ menu: base => ({ ...base, position: 'static', boxShadow: 'none', maxHeight: '60vh' }) }}
          filterOption={customFilterOption}
          components={{
            Option: CustomOption,
            ...(loading ? {
              LoadingIndicator: () => <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
            } : {})
          }}
        />
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default KtmbTimetable; 